/**
 * Overrides de **présentation** d'un tableau paramétrable : masquage, ordre,
 * largeur et renommage des colonnes.
 *
 * Ces overrides vivent côté front (`tableConfig.columns`) et sont réappliqués
 * après **chaque** lecture de la table — les clés de colonnes sont régénérées à
 * chaque fois, d'où le matching par `label` / `title`, seul identifiant stable.
 * Les axes « donnée » (mode, variante, consolidation…) sont, eux, résolus côté
 * backend via les query params.
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

import { resolveTableConfig } from './tableDescriptors';

// Applique les overrides de PRÉSENTATION d'un tableau paramétrable (BPU) sur le
// jeu de colonnes complet renvoyé par l'API : masquage, réordonnancement et
// largeur des colonnes. Ces overrides vivent côté front (box.tableConfig.columns)
// et sont réappliqués après CHAQUE (re)fetch — sinon un refresh écraserait la
// perso (les clés de colonnes sont régénérées à chaque fetch, d'où le matching
// par `label`/`title`, stable). Les axes "donnée" (mode/consolidation/variant)
// sont eux résolus côté backend via les query params.
// Un même libellé peut arriver sous deux formes Unicode (« é » en un caractère
// ou « e » + accent) : le Drawer et l'API ne l'écrivent pas toujours pareil.
const labelKey = (label) => (typeof label === 'string' ? label.normalize('NFC') : label);

// `tableName` : la config est d'abord relue dans le modèle courant de la famille
// (cf. resolveTableConfig).
export const applyTableConfigPresentation = (content, rawTableConfig, boxWidth, tableName = null) => {
  const tableConfig = tableName ? resolveTableConfig(tableName, rawTableConfig) : rawTableConfig;
  const overrides = tableConfig?.columns;
  if (!Array.isArray(overrides) || overrides.length === 0) return content;

  const byLabel = new Map(overrides.map((c) => [labelKey(c.label), c]));
  const overrideOf = (label) => byLabel.get(labelKey(label));
  const originalIndex = new Map(
    content.columns.map((col, idx) => [col.key, idx])
  );

  let columns = content.columns
    .filter((col) => !overrideOf(col.title)?.hidden)
    .sort((a, b) => {
      const oa = overrideOf(a.title)?.order ?? originalIndex.get(a.key);
      const ob = overrideOf(b.title)?.order ?? originalIndex.get(b.key);
      return oa - ob;
    });

  if (columns.length === 0) {
    return { ...content, columns: [], columnsDisposition: [] };
  }

  // Largeurs en POURCENT (stable vs redimensionnement de la box) : override.width
  // prioritaire, sinon dérivée de la largeur px renvoyée par l'API. On renormalise
  // sur les colonnes visibles pour remplir la box.
  const pct = columns.map((col) => {
    const override = overrideOf(col.title)?.width;
    if (override > 0) return override;
    return boxWidth > 0
      ? ((col.width || 0) / boxWidth) * 100
      : 100 / columns.length;
  });
  const pctSum = pct.reduce((acc, value) => acc + value, 0) || 1;

  // customLabel = renommage de PRÉSENTATION. Le matching (ci-dessus) reste sur le
  // label ORIGINAL (col.title issu du fetch). On renomme le titre affiché ET la
  // clé data de chaque row EN MÊME TEMPS : en aval, buildUniqueContentData remappe
  // les valeurs via row[col.title] ; si on ne renommait que le titre, il lirait
  // row["Nouveau nom"] === undefined → cellules vides (bug observé). Un reset
  // (customLabel vidé) fait réapparaître le nom d'origine ; le refetch rematche
  // toujours sur le label original.
  const renames = {};
  columns.forEach((col) => {
    const custom = overrideOf(col.title)?.customLabel;
    if (custom && custom !== col.title) renames[col.title] = custom;
  });

  columns = columns.map((col, i) => ({
    ...col,
    width: (pct[i] / pctSum) * boxWidth,
    // Alignement choisi dans le document : sans lui, chaque relecture de la
    // table rendait celui de l'API.
    ...(overrideOf(col.title)?.align ? { textAlign: overrideOf(col.title).align } : {}),
    ...(overrideOf(col.title)?.verticalAlign ? { verticalAlign: overrideOf(col.title).verticalAlign } : {}),
    ...(overrideOf(col.title)?.headerAlign ? { headerTextAlign: overrideOf(col.title).headerAlign } : {}),
    // Ancre = label d'origine (clé de matching des overrides). Permet à une
    // édition d'en-tête au canvas de retrouver la bonne entrée customLabel :
    // canvas et Drawer ciblent ainsi la MÊME valeur.
    originalTitle: col.title,
    title: renames[col.title] || col.title,
  }));

  let data = content.data;
  if (Object.keys(renames).length > 0 && Array.isArray(content.data)) {
    data = content.data.map((row) => {
      const next = { ...row };
      Object.entries(renames).forEach(([original, custom]) => {
        if (Object.prototype.hasOwnProperty.call(next, original)) {
          next[custom] = next[original];
          delete next[original];
        }
      });
      return next;
    });
  }

  const survivingKeys = new Set(columns.map((col) => col.key));
  const columnsDisposition = (content.columnsDisposition || [])
    .filter((col) => survivingKeys.has(col.key))
    .sort(
      (a, b) =>
        columns.findIndex((c) => c.key === a.key) -
        columns.findIndex((c) => c.key === b.key)
    );

  return { ...content, columns, columnsDisposition, data };
};
